import React, { useEffect, useState } from "react";
import { AiOutlineSend } from "react-icons/ai";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUsers,
  FiSun,
  FiAlertTriangle,
} from "react-icons/fi";
import { Form, Button, Container, Row, Col, Card, Alert } from "react-bootstrap";
import { getDestinos, cotizar } from "../services/cotizacionApi";
import VehiculoCard from "./VehiculoCard";
import CustomSelect from "./CustomSelect";
import PassengerStepper from "./PassengerStepper";
import {
  formatFechaBonita,
  formatHoraBonita,
  getTodayISODate,
} from "../utils/formatDateTime";
import "./FormCotizacion.css";

const ID_SOLO_UN_RECORRIDO = "1";

const TEMPORADA_OPTIONS = [
  { value: "Baja", label: "Temporada baja" },
  { value: "Alta", label: "Temporada alta" },
];

const FormCotizacion = () => {
  const [formData, setFormData] = useState({
    fechaIda: "",
    lugarSalida: "",
    horaIda: "",
    fechaRegreso: "",
    destino: "",
    horaRegreso: "",
    temporada: "Baja",
    numPasajeros: 1,
    noches: 0,
  });

  const [vehiculos, setVehiculos] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [destinosError, setDestinosError] = useState(false);
  const [loadingDestinos, setLoadingDestinos] = useState(true);
  const [loading, setLoading] = useState(false);
  const [avisoPasajeros, setAvisoPasajeros] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const destinoDeshabilitado = formData.lugarSalida === ID_SOLO_UN_RECORRIDO;

  // Obtener el listado de destinos al montar el componente
  useEffect(() => {
    const fetchDestinos = async () => {
      setLoadingDestinos(true);
      setDestinosError(false);
      try {
        const data = await getDestinos();
        setDestinos(data);
      } catch (error) {
        console.error("Error al obtener los destinos", error);
        setDestinosError(true);
      } finally {
        setLoadingDestinos(false);
      }
    };

    fetchDestinos();
  }, []);

  // Si el lugar de salida solo admite un recorrido, limpiamos el destino
  useEffect(() => {
    if (destinoDeshabilitado && formData.destino !== "") {
      setFormData((prev) => ({ ...prev, destino: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinoDeshabilitado]);

  // Si la fecha de regreso queda antes de la nueva fecha de ida, la limpiamos
  useEffect(() => {
    if (formData.fechaRegreso && formData.fechaRegreso < formData.fechaIda) {
      setFormData((prev) => ({ ...prev, fechaRegreso: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fechaIda]);

  const destinoOptions = destinos.map((destino) => ({
    value: destino.id,
    label: destino.nombre,
  }));

  const destinosEmptyMessage = destinosError
    ? "No se pudieron cargar los lugares disponibles."
    : loadingDestinos
    ? "Cargando lugares disponibles..."
    : "No hay lugares disponibles.";

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    handleFieldChange(e.target.name, e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (!formData.lugarSalida || !formData.temporada) {
      setErrorMessage("Por favor completa el lugar de salida y la temporada.");
      return;
    }

    if (!destinoDeshabilitado && !formData.destino) {
      setErrorMessage("Por favor selecciona un lugar de destino.");
      return;
    }

    // Validar que la fecha de ida no sea anterior a hoy
    if (formData.fechaIda < getTodayISODate()) {
      setErrorMessage("La fecha de ida no puede ser anterior a hoy.");
      return;
    }

    // Validar que la fecha de regreso no sea menor a la fecha de ida
    if (formData.fechaRegreso < formData.fechaIda) {
      setErrorMessage(
        "La fecha de regreso no puede ser menor a la fecha de ida."
      );
      return;
    }

    // Calcular el número de noches
    const fechaIda = new Date(`${formData.fechaIda}T${formData.horaIda}`);
    const fechaRegreso = new Date(
      `${formData.fechaRegreso}T${formData.horaRegreso}`
    );

    // Validar si la fecha de regreso es el mismo día y la hora de regreso es menor a la de ida
    if (
      formData.fechaIda === formData.fechaRegreso &&
      fechaRegreso <= fechaIda
    ) {
      setErrorMessage(
        "La hora de regreso no puede ser menor a la hora de ida si es el mismo día."
      );
      return;
    }

    // Si la hora de regreso es antes de las 24:00, ajustamos la fecha de regreso
    const ajusteHoraRegreso = fechaRegreso.getHours() < 24 ? 1 : 0;
    const noches = Math.max(
      0,
      Math.ceil((fechaRegreso - fechaIda) / (1000 * 3600 * 24)) -
        ajusteHoraRegreso
    );

    // Actualizar formData con el número de noches
    setFormData((prev) => ({ ...prev, noches }));

    setErrorMessage(""); // Limpiar mensajes de error
    setLoading(true);
    setAvisoPasajeros(formData.numPasajeros > 40); // Aviso si supera 40 pasajeros

    try {
      const vehiculosData = await cotizar({ ...formData, noches });
      setVehiculos(vehiculosData);
      setHasSearched(true);
    } catch (error) {
      console.error("Error al enviar el formulario", error);
      setErrorMessage(
        "Error al obtener los vehículos disponibles. Intente nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="cotizador-container">
      <Card className="cotizacion-card">
        <span className="cotizacion-eyebrow">Cotiza en minutos</span>
        <h2 className="cotizacion-title">Cotización de Transporte Especial</h2>
        <p className="cotizacion-subtitle">
          Completa los datos de tu viaje y te mostraremos los vehículos
          disponibles al instante.
        </p>

        {destinosError && (
          <Alert variant="danger" className="mt-2 cotiz-alert" role="alert">
            <FiAlertTriangle aria-hidden="true" />
            No se pudieron cargar los lugares disponibles. Verifica tu
            conexión o intenta recargar la página.
          </Alert>
        )}

        <Form onSubmit={handleSubmit} className="cotizacion-form" noValidate>
          <Row className="cotiz-row">
            <Col md={6}>
              <Form.Group controlId="fechaIda" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiCalendar className="form-label-icon" aria-hidden="true" />
                  Fecha de Ida
                </Form.Label>
                <Form.Control
                  type="date"
                  name="fechaIda"
                  value={formData.fechaIda}
                  onChange={handleInputChange}
                  min={getTodayISODate()}
                  required
                />
                {formData.fechaIda && (
                  <span className="field-hint">
                    <FiCalendar aria-hidden="true" />
                    {formatFechaBonita(formData.fechaIda)}
                  </span>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="horaIda" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiClock className="form-label-icon" aria-hidden="true" />
                  Hora de Ida
                </Form.Label>
                <Form.Control
                  type="time"
                  name="horaIda"
                  value={formData.horaIda}
                  onChange={handleInputChange}
                  required
                />
                {formData.horaIda && (
                  <span className="field-hint">
                    <FiClock aria-hidden="true" />
                    {formatHoraBonita(formData.horaIda)}
                  </span>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="cotiz-row">
            <Col md={6}>
              <Form.Group controlId="lugarSalida" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiMapPin className="form-label-icon" aria-hidden="true" />
                  Lugar de salida
                </Form.Label>
                <CustomSelect
                  name="lugarSalida"
                  label="Lugar de salida"
                  icon={FiMapPin}
                  options={destinoOptions}
                  value={formData.lugarSalida}
                  onChange={handleFieldChange}
                  placeholder="Selecciona tu lugar de salida"
                  required
                  isInvalid={submitAttempted && !formData.lugarSalida}
                  emptyMessage={destinosEmptyMessage}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="temporada" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiSun className="form-label-icon" aria-hidden="true" />
                  Temporada
                </Form.Label>
                <CustomSelect
                  name="temporada"
                  label="Temporada"
                  icon={FiSun}
                  options={TEMPORADA_OPTIONS}
                  value={formData.temporada}
                  onChange={handleFieldChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="cotiz-row">
            <Col md={6}>
              <Form.Group controlId="fechaRegreso" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiCalendar className="form-label-icon" aria-hidden="true" />
                  Fecha de Regreso
                </Form.Label>
                <Form.Control
                  type="date"
                  name="fechaRegreso"
                  value={formData.fechaRegreso}
                  onChange={handleInputChange}
                  min={formData.fechaIda || getTodayISODate()} // No puede ser anterior a la fecha de ida
                  required
                />
                {formData.fechaRegreso && (
                  <span className="field-hint">
                    <FiCalendar aria-hidden="true" />
                    {formatFechaBonita(formData.fechaRegreso)}
                  </span>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="horaRegreso" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiClock className="form-label-icon" aria-hidden="true" />
                  Hora de Regreso
                </Form.Label>
                <Form.Control
                  type="time"
                  name="horaRegreso"
                  value={formData.horaRegreso}
                  onChange={handleInputChange}
                  required
                  min={
                    formData.fechaIda === formData.fechaRegreso
                      ? formData.horaIda // Si es el mismo día, la hora mínima de regreso será la de ida
                      : null // De lo contrario, no hay restricción
                  }
                />
                {formData.horaRegreso && (
                  <span className="field-hint">
                    <FiClock aria-hidden="true" />
                    {formatHoraBonita(formData.horaRegreso)}
                  </span>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="cotiz-row">
            <Col md={6}>
              <Form.Group controlId="destino" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiMapPin className="form-label-icon" aria-hidden="true" />
                  Destino
                </Form.Label>
                <CustomSelect
                  name="destino"
                  label="Destino"
                  icon={FiMapPin}
                  options={destinoOptions}
                  value={formData.destino}
                  onChange={handleFieldChange}
                  placeholder={
                    destinoDeshabilitado
                      ? "No requiere destino"
                      : "Selecciona un lugar de destino"
                  }
                  disabled={destinoDeshabilitado}
                  required={!destinoDeshabilitado}
                  isInvalid={
                    submitAttempted &&
                    !destinoDeshabilitado &&
                    !formData.destino
                  }
                  emptyMessage={destinosEmptyMessage}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="numPasajeros" className="cotiz-field">
                <Form.Label className="form-label">
                  <FiUsers className="form-label-icon" aria-hidden="true" />
                  Número de Pasajeros
                </Form.Label>
                <PassengerStepper
                  name="numPasajeros"
                  value={formData.numPasajeros}
                  onChange={handleFieldChange}
                  min={1}
                  label="Número de pasajeros"
                />
              </Form.Group>
            </Col>
          </Row>

          {avisoPasajeros && (
            <Alert variant="warning" className="mt-2 cotiz-alert">
              <FiAlertTriangle aria-hidden="true" />
              Requerirá más de un vehículo.
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="danger" className="mt-2 cotiz-alert" role="alert">
              <FiAlertTriangle aria-hidden="true" />
              {errorMessage}
            </Alert>
          )}

          <Button
            variant="primary"
            type="submit"
            className="mt-3 w-100 btn-cotizar"
            disabled={loading}
          >
            {loading ? (
              <span className="btn-spinner" aria-hidden="true" />
            ) : (
              <AiOutlineSend aria-hidden="true" />
            )}
            {loading ? "Buscando disponibilidad..." : "Cotizar Viaje"}
          </Button>
        </Form>

        {hasSearched && (
          <div className="resultados-cotizacion" aria-live="polite">
            <h3 className="resultados-title">
              {vehiculos.length > 0
                ? "Vehículos disponibles"
                : "Sin resultados"}
            </h3>
            <Row>
              {vehiculos.length > 0 ? (
                vehiculos.map((vehiculo, index) => (
                  <VehiculoCard
                    key={vehiculo.id}
                    vehiculo={vehiculo}
                    index={index}
                  />
                ))
              ) : (
                <p className="resultados-empty">
                  No hay vehículos disponibles para la cantidad de pasajeros
                  ingresada.
                </p>
              )}
            </Row>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default FormCotizacion;
